import { zodResolver } from '@hookform/resolvers/zod';
import type { ClipboardEvent, KeyboardEvent } from 'react';
import type { Control, UseFormSetValue } from 'react-hook-form';
import { useForm, useWatch } from 'react-hook-form';

import { DateRangePicker } from '@/components/common/date-picker';
import { Drawer } from '@/components/feedback/drawer';
import { MultiSelectField, NumberField } from '@/components/forms';
import { useCursorSelect } from '@/hooks/use-cursor-select';
import { appTzLabel, fromDateRange, toDayjsRange } from '@/lib/date';

import { listReportCategories, listReportCustomers, listReportDevices } from '../../api';
import { reportKeys } from '../../hooks';
import { isNumericKey, stripToNumeric } from '../../numeric-input';
import { toDefaults, toFilters } from '../filters';
import { deviceActivityFiltersSchema } from '../schemas';
import type { DeviceActivityFilterFormValues, DeviceActivityListFilters } from '../types';

import styles from './device-activity-filter-drawer.module.scss';

type Draft = DeviceActivityFilterFormValues;

/** All three dropdowns are `{ id, name }` — the shipped meta contract. */
const toOption = (o: { id: string; name: string }) => ({ value: o.id, label: o.name });

/** The six numeric bound fields — the only paths a `RangeRow` may drive. Narrowing to these (rather
 *  than any `FieldPath<Draft>`) is what makes the paste handler's `setValue(name, number | null)`
 *  well-typed, and stops the two date bounds being wired into a numeric row by mistake. */
type NumericBound = {
  [K in keyof Draft]-?: Draft[K] extends number | null ? K : never;
}[keyof Draft];

type RangeRowProps = {
  label: string;
  control: Control<Draft>;
  setValue: UseFormSetValue<Draft>;
  fromName: NumericBound;
  toName: NumericBound;
  /** `0` for a whole-number metric; omit for one that legitimately carries decimals. */
  precision?: number;
};

/** One From/To pair: two `<NumberField>`s side by side under a single legend (Figma 294:259875 —
 *  16px gap, `From`/`To` placeholders, no per-input label). Same shape as
 *  `customer-order-filter-drawer.tsx`'s Order Amount block; three call sites here, so it is one
 *  local component rather than three copies.
 *
 *  It renders no error element: the zod issue is attached to the `To` path, so that field's own
 *  `<FormField>` prints the message and sets the input's error status. */
function RangeRow({ label, control, setValue, fromName, toName, precision }: RangeRowProps) {
  const allowDecimal = precision !== 0;

  /** AC10: antd's `InputNumber` accepts any text while typing and only parses on blur, so the
   *  digits-only rule has to be enforced on the input itself — reject a non-numeric keystroke, and
   *  replace a paste with its numeric residue. A paste into a range box is a whole value in
   *  practice, so the stripped text replaces the field rather than splicing at the caret. */
  const guard = (name: NumericBound) => ({
    onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => {
      // Never swallow a shortcut (Ctrl+A, Cmd+V, …) — only bare printable characters.
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      if (!isNumericKey(event.key, allowDecimal)) event.preventDefault();
    },
    onPaste: (event: ClipboardEvent<HTMLInputElement>) => {
      event.preventDefault();
      const clean = stripToNumeric(event.clipboardData.getData('text'), allowDecimal);
      // A paste with no usable digits ("abc", or a bare ".") clears the bound rather than writing
      // `NaN` — `Number('.')` and `Number('')` are not interchangeable, and NaN would reach zod.
      const parsed = Number(clean);
      setValue(name, clean === '' || Number.isNaN(parsed) ? null : parsed, {
        shouldValidate: true,
      });
    },
  });

  return (
    <fieldset className={styles.fieldset}>
      <legend className={styles.legend}>{label}</legend>
      <div className={styles.row}>
        <NumberField
          name={fromName}
          control={control}
          aria-label={`${label} From`}
          placeholder="From"
          min={0}
          precision={precision}
          style={{ width: '100%' }}
          {...guard(fromName)}
        />
        <NumberField
          name={toName}
          control={control}
          aria-label={`${label} To`}
          placeholder="To"
          min={0}
          precision={precision}
          style={{ width: '100%' }}
          {...guard(toName)}
        />
      </div>
    </fieldset>
  );
}

export type DeviceActivityFilterDrawerProps = {
  open: boolean;
  /** Last applied filters; the draft re-seeds from this on each open. */
  value: DeviceActivityListFilters;
  onApply: (filters: DeviceActivityListFilters) => void;
  onClose: () => void;
};

type BodyProps = Omit<DeviceActivityFilterDrawerProps, 'open'>;

function FilterBody({ value, onApply, onClose }: BodyProps) {
  const draft = toDefaults(value);
  const form = useForm<Draft>({
    resolver: zodResolver(deviceActivityFiltersSchema),
    defaultValues: {
      ...draft,
      // The two purchase-date bounds are `enumField`s, so `toDefaults` maps an unset one to
      // `undefined`; antd clears to `null`, and an `undefined` RHF value silently reverts to the
      // default. Coalesce — never hardcode `null`, which would clobber the applied range on reopen.
      purchaseDateFrom: draft.purchaseDateFrom ?? null,
      purchaseDateTo: draft.purchaseDateTo ?? null,
    },
    mode: 'onBlur',
  });

  // Three cursor-paged, server-searched sources. Each key varies by term so the cursor resets
  // when the search changes; the drawer unmounts on close, so no `resetSearch()` wiring is needed.
  const customers = useCursorSelect({
    queryKey: reportKeys.metaCustomers,
    fetchPage: listReportCustomers,
    toOption,
  });
  const devices = useCursorSelect({
    queryKey: reportKeys.metaDevices,
    fetchPage: listReportDevices,
    toOption,
  });
  const categories = useCursorSelect({
    queryKey: reportKeys.metaCategories,
    fetchPage: listReportCategories,
    toOption,
  });

  const handleApply = form.handleSubmit((values) => {
    onApply(toFilters(values));
    onClose();
  });

  const [purchaseFrom, purchaseTo] = useWatch({
    control: form.control,
    name: ['purchaseDateFrom', 'purchaseDateTo'],
  });

  // AC11: Apply is blocked while any of the three numeric pairs is inverted. The error always
  // lands on the `To` path. The date pair has no guard (plan D3).
  const { totalActiveTimeTo, avgRuntimePerDayTo, totalAlertsTo } = form.formState.errors;
  const rangeError = Boolean(totalActiveTimeTo || avgRuntimePerDayTo || totalAlertsTo);

  // No `width`: the shared Drawer already defaults to the Figma panel width (464px, 32:193227).
  return (
    <Drawer
      open
      onClose={onClose}
      title="Filter"
      primaryText="Apply"
      onPrimary={handleApply}
      primaryDisabled={rangeError}
      cancelText="Cancel"
      onCancel={onClose}
    >
      {/* Labels are the tab's column headers verbatim (plan D1), so the panel and the grid read
          the same words. Field order is README AC1 and Figma 294:259871, which agree. */}
      <MultiSelectField
        name="customerIds"
        control={form.control}
        label="Customer"
        placeholder="Select"
        showSearch={customers.showSearch}
        loading={customers.loading}
        onPopupScroll={customers.onPopupScroll}
        options={customers.options}
      />

      <MultiSelectField
        name="deviceIds"
        control={form.control}
        label="Device"
        placeholder="Select"
        showSearch={devices.showSearch}
        loading={devices.loading}
        onPopupScroll={devices.onPopupScroll}
        options={devices.options}
      />

      <MultiSelectField
        name="categoryIds"
        control={form.control}
        label="Category"
        placeholder="Select"
        showSearch={categories.showSearch}
        loading={categories.loading}
        onPopupScroll={categories.onPopupScroll}
        options={categories.options}
      />

      <RangeRow
        label="Total Active time"
        control={form.control}
        setValue={form.setValue}
        fromName="totalActiveTimeFrom"
        toName="totalActiveTimeTo"
        precision={0}
      />

      {/* No `precision`: the metric is total runtime divided by days, so decimals are legitimate. */}
      <RangeRow
        label="Avg Runtime/day"
        control={form.control}
        setValue={form.setValue}
        fromName="avgRuntimePerDayFrom"
        toName="avgRuntimePerDayTo"
      />

      {/* Figma 294:259903 misspells this "Genearated"; build the AC spelling (README). */}
      <RangeRow
        label="Total No of Alerts Generated"
        control={form.control}
        setValue={form.setValue}
        fromName="totalAlertsFrom"
        toName="totalAlertsTo"
        precision={0}
      />

      <fieldset className={styles.fieldset}>
        <legend className={styles.legend}>{appTzLabel('Purchase Date')}</legend>
        {/* AC2 overrides Figma's single `mm/dd/yyyy` box. antd's RangePicker disables dates before
            the From for the To end, which is AC12, so no `disabledDate` is passed. Bound to the two
            scalar draft fields rather than a tuple — that is the shape the wire takes. */}
        <DateRangePicker
          aria-label={appTzLabel('Purchase Date')}
          format="MM/DD/YYYY"
          style={{ width: '100%' }}
          value={toDayjsRange(purchaseFrom && purchaseTo ? [purchaseFrom, purchaseTo] : null)}
          onChange={(range) => {
            const iso = fromDateRange(range);
            form.setValue('purchaseDateFrom', iso?.[0] ?? null, { shouldValidate: true });
            form.setValue('purchaseDateTo', iso?.[1] ?? null, { shouldValidate: true });
          }}
        />
      </fieldset>
    </Drawer>
  );
}

export function DeviceActivityFilterDrawer({ open, ...rest }: DeviceActivityFilterDrawerProps) {
  if (!open) {
    return (
      <Drawer open={false} onClose={rest.onClose}>
        {null}
      </Drawer>
    );
  }
  // Unmount on close; remounts fresh on open so the draft re-seeds from the last applied `value`
  // (AC5) and an abandoned edit cannot survive (AC4).
  return <FilterBody {...rest} />;
}
